<template>
  <div class="pi-demo" :class="['pi-demo--' + variant, { 'is-wide': wide }]">
    <p v-if="variant === 'compact'" class="pi-demo__heading">
      Empresa de demonstração<pi-definition-tooltip class="pi-demo__info" info-key="common.demo" label="Empresa de demonstração"/>
    </p>
    <p v-if="request.loading" class="pi-demo__muted" role="status">Conferindo a marca de demonstração…</p>

    <template v-else-if="variant === 'compact'">
      <p v-if="view.known" class="pi-demo__state">
        <strong>{{ view.isDemo ? "Sim" : "Não" }}</strong>
        <template v-if="view.isDemo"> · fora dos números do PI e dos relatórios do Master<template v-if="view.detailText"> · {{ view.detailText }}</template></template>
        <template v-else-if="view.lastText"> · {{ view.lastText }}</template>
      </p>
      <p v-if="view.isDemo && view.note" class="pi-demo__note">Observação: {{ view.note }}</p>
      <button v-if="view.editable" ref="trigger" type="button" class="pi-btn pi-btn--ghost pi-demo__compact-btn" :disabled="busy"
              @click="openDialog(view.isDemo ? 'unmark' : 'mark')">{{ view.isDemo ? "Desmarcar" : "Marcar como demonstração" }}</button>
    </template>

    <template v-else>
      <div v-if="view.isDemo" class="pi-demo__banner" role="region" :aria-labelledby="ids.banner">
        <p class="pi-demo__banner-text">
          <strong :id="ids.banner">{{ bannerTitle }}</strong><template v-if="view.detailText"> {{ view.detailText }}.</template>
          <pi-definition-tooltip class="pi-demo__info" info-key="common.demo" label="Empresa de demonstração"/>
        </p>
        <p v-if="view.note" class="pi-demo__note">Observação: {{ view.note }}</p>
        <button v-if="view.editable" ref="trigger" type="button" class="pi-btn pi-btn--ghost pi-demo__unmark" :disabled="busy"
                @click="openDialog('unmark')">Desmarcar</button>
      </div>
      <button v-else-if="view.editable" ref="trigger" type="button" class="pi-demo__link" :disabled="busy"
              @click="openDialog('mark')">Marcar como demonstração</button>
    </template>

    <p v-if="view.deleted && !request.loading" class="pi-demo__muted">Empresa excluída: a marca de demonstração não pode ser alterada.</p>
    <p v-if="request.error && !request.loading" class="pi-demo__error" role="alert">
      {{ request.error }}
      <button type="button" class="pi-demo__retry" @click="load">Tentar de novo</button>
    </p>
    <p v-if="view.pendingText && !notice" class="pi-demo__pending">{{ view.pendingText }}</p>
    <p v-if="notice" class="pi-demo__notice" :class="{ 'is-pending': noticePending }" role="status">{{ notice }}</p>

    <div v-if="dialog.open" class="pi-demo__overlay">
      <div ref="dialog" class="pi-demo__dialog" role="dialog" aria-modal="true" :aria-labelledby="ids.title" :aria-describedby="ids.effect"
           @keydown.esc="cancel" @keydown.tab="trapFocus">
        <h3 :id="ids.title" class="pi-demo__title">{{ dialogTitle }}</h3>
        <p v-if="companyName" class="pi-demo__company">{{ companyName }}<span v-if="shardCode" class="pi-demo__shard"> · banco {{ shardCode }}</span></p>
        <p :id="ids.effect" class="pi-demo__effect">{{ dialogEffect }}</p>
        <form class="pi-demo__form" novalidate @submit.prevent="submit">
          <fieldset v-if="marking" class="pi-demo__reasons" :aria-describedby="reasonProblem ? ids.reasonError : null">
            <legend class="pi-demo__legend">
              Motivo<pi-definition-tooltip class="pi-demo__info" info-key="companies.demoReason" label="Motivo"/>
            </legend>
            <label v-for="option in reasons" :key="option.value" class="pi-demo__option">
              <input v-model="dialog.reason" type="radio" :name="ids.reason" :value="option.value" :disabled="busy">
              {{ option.label }}
            </label>
            <span v-if="reasonProblem" :id="ids.reasonError" class="pi-demo__field-error">{{ reasonProblem }}</span>
          </fieldset>
          <div class="pi-demo__field">
            <span class="pi-demo__label">
              <label :for="ids.note">{{ noteLabel }}</label>
              <pi-definition-tooltip class="pi-demo__info" info-key="companies.demoNote" label="Observação"/>
            </span>
            <textarea :id="ids.note" ref="note" v-model="dialog.note" class="form-control pi-input pi-demo__textarea" rows="3" :maxlength="noteLimit"
                      :disabled="busy" :aria-invalid="noteProblem ? 'true' : 'false'"
                      :aria-describedby="noteProblem ? ids.noteError + ' ' + ids.noteCount : ids.noteCount"></textarea>
            <span :id="ids.noteCount" class="pi-demo__count">{{ noteCount }}</span>
            <span v-if="noteProblem" :id="ids.noteError" class="pi-demo__field-error">{{ noteProblem }}</span>
          </div>
          <p v-if="dialog.failure" class="pi-demo__error" role="alert">{{ dialog.failure }}</p>
          <div class="pi-demo__actions">
            <button type="submit" class="pi-btn" :disabled="busy">{{ busy ? "Salvando…" : submitLabel }}</button>
            <button type="button" class="pi-btn pi-btn--ghost" :disabled="busy" @click="cancel">Cancelar</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import piDefinitionTooltip from "./piDefinitionTooltip.vue";
import { setCompanyDemo } from "./piApi.js";
import {
  DEMO_BANNER_TITLE, DEMO_NOTE_LIMIT, DEMO_REASONS, OTHER_REASON, OUTSIDE_PI, blankDemoForm, demoDoneText, demoEffectText, demoFormProblems,
  demoFormValid, demoView, legacyDemoBody, legacyDemoState, legacyErrorText, piCountsShard, piDemoBody,
} from "./piCompanyDemoModel.js";
import { REQUEST_SERVICE } from "../../../../requestConfig";
import { COMPANY_HIDE_FROM_REPORTS } from "../../../../urlConfig";

const NOTICE_MS = 10000;

const FOCUSABLE = "button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href]";

let sequence = 0;

export default {
  name: "pi-company-demo",
  components: { piDefinitionTooltip },
  props: {
    companyId: { type: String, required: true },
    shardCode: { type: String, required: true },
    companyName: { type: String, default: "" },
    pi: { type: Object, default: null },
    variant: { type: String, default: "page" },
  },
  data() {
    sequence += 1;
    const prefix = "pi-demo-" + sequence;
    return {
      ids: {
        banner: prefix + "-banner",
        title: prefix + "-title",
        effect: prefix + "-effect",
        reason: prefix + "-reason",
        reasonError: prefix + "-reason-error",
        note: prefix + "-note",
        noteCount: prefix + "-note-count",
        noteError: prefix + "-note-error",
      },
      reasons: DEMO_REASONS,
      noteLimit: DEMO_NOTE_LIMIT,
      bannerTitle: DEMO_BANNER_TITLE,
      request: { loading: false, loaded: false, error: "" },
      legacy: null,
      dialog: Object.assign({ open: false, failure: "" }, blankDemoForm("mark")),
      showProblems: false,
      busy: false,
      notice: "",
      noticePending: false,
    };
  },
  computed: {
    view() {
      return demoView(this.legacy, this.pi);
    },
    wide() {
      return this.variant === "page" && (this.view.isDemo || !!this.request.error || !!this.notice || !!this.view.pendingText);
    },
    marking() {
      return this.dialog.mode !== "unmark";
    },
    problems() {
      return demoFormProblems(this.dialog);
    },
    reasonProblem() {
      return this.showProblems ? this.problems.reason : "";
    },
    noteProblem() {
      return this.showProblems ? this.problems.note : "";
    },
    noteLabel() {
      return this.marking && this.dialog.reason === OTHER_REASON ? "Observação (obrigatória)" : "Observação (opcional)";
    },
    noteCount() {
      const length = String(this.dialog.note || "").length;
      return length + " de " + this.noteLimit + " caracteres";
    },
    dialogTitle() {
      return this.marking ? "Marcar como empresa de demonstração" : "Desmarcar a empresa de demonstração";
    },
    dialogEffect() {
      return demoEffectText(this.marking, this.shardCode);
    },
    submitLabel() {
      return this.marking ? "Marcar como demonstração" : "Desmarcar";
    },
    requestKey() {
      return this.companyId + "/" + this.shardCode;
    },
  },
  watch: {
    requestKey() {
      this.load();
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.clearNotice();
  },
  methods: {
    load() {
      if (!this.companyId || !this.shardCode) return;
      const key = this.requestKey;
      this.request = { loading: true, loaded: false, error: "" };
      REQUEST_SERVICE.requestContext("GET", COMPANY_HIDE_FROM_REPORTS(this.companyId), this.shardCode)
        .then((response) => {
          if (key !== this.requestKey) return;
          this.legacy = legacyDemoState(response && response.data);
          this.request = { loading: false, loaded: true, error: "" };
        })
        .catch((error) => {
          if (key !== this.requestKey) return;
          this.legacy = null;
          this.request = { loading: false, loaded: false, error: legacyErrorText(error, "conferir a marca de demonstração") };
        });
    },
    clearNotice() {
      if (this.noticeTimer) clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    },
    showNotice(text, pending) {
      this.clearNotice();
      this.notice = text;
      this.noticePending = pending === true;
      this.noticeTimer = setTimeout(() => {
        this.notice = "";
        this.noticeTimer = null;
      }, NOTICE_MS);
    },
    openDialog(mode) {
      if (this.busy) return;
      this.returnFocus = typeof document !== "undefined" ? document.activeElement : null;
      this.dialog = Object.assign({ open: true, failure: "" }, blankDemoForm(mode));
      this.showProblems = false;
      this.$nextTick(() => this.focusFirst());
    },
    focusables() {
      const panel = this.$refs.dialog;
      return panel ? Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE)) : [];
    },
    focusFirst() {
      const panel = this.$refs.dialog;
      if (!panel) return;
      const target = this.marking ? panel.querySelector("input[type=radio]") : this.$refs.note;
      if (target && typeof target.focus === "function") target.focus();
    },
    trapFocus(event) {
      const nodes = this.focusables();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    closeDialog() {
      this.dialog = Object.assign({ open: false, failure: "" }, blankDemoForm("mark"));
      this.showProblems = false;
      this.$nextTick(() => {
        const back = this.returnFocus && document.body.contains(this.returnFocus) ? this.returnFocus : this.$refs.trigger;
        if (back && typeof back.focus === "function") back.focus();
        this.returnFocus = null;
      });
    },
    cancel() {
      if (this.busy) return;
      this.closeDialog();
    },
    submit() {
      if (this.busy) return;
      this.showProblems = true;
      if (!demoFormValid(this.dialog)) {
        this.$nextTick(() => {
          const target = this.problems.reason ? this.$refs.dialog.querySelector("input[type=radio]") : this.$refs.note;
          if (target && typeof target.focus === "function") target.focus();
        });
        return;
      }
      const form = { mode: this.dialog.mode, reason: this.dialog.reason, note: this.dialog.note };
      const marking = form.mode !== "unmark";
      this.busy = true;
      this.dialog.failure = "";
      REQUEST_SERVICE.requestContext("PUT", COMPANY_HIDE_FROM_REPORTS(this.companyId), this.shardCode, legacyDemoBody(form))
        .then((response) => {
          this.legacy = legacyDemoState(response && response.data);
          this.request = { loading: false, loaded: true, error: "" };
          if (!piCountsShard(this.shardCode)) return OUTSIDE_PI;
          return setCompanyDemo(this.companyId, piDemoBody(form, this.shardCode)).then(() => true, () => false);
        }, (error) => {
          this.busy = false;
          this.dialog.failure = legacyErrorText(error, marking ? "marcar a empresa como demonstração" : "desmarcar a empresa");
          return null;
        })
        .then((piApplied) => {
          if (piApplied === null) return;
          this.busy = false;
          this.closeDialog();
          this.showNotice(demoDoneText(marking, piApplied), piApplied === false);
          this.$emit("changed", { isDemo: marking, piApplied: piApplied === true });
        });
    },
  },
};
</script>

<style src="./pi.css"></style>

<style scoped>
.pi-demo--page.is-wide {
  flex: 1 1 100%;
}

.pi-demo--compact {
  padding: 0 20px;
  border-right: 1px dashed #ced0d1;
}

.pi-demo__heading {
  display: flex;
  align-items: center;
  min-height: 40px;
  margin: 11px 0;
  font-size: 14px;
  color: #9b9b9b;
}

.pi-demo__compact-btn {
  margin-top: 6px;
}

.pi-demo__muted {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.pi-demo__link {
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.pi-demo__link:hover,
.pi-demo__link:focus {
  color: #1340b5;
}

.pi-demo__link:focus,
.pi-demo__retry:focus,
.pi-demo__unmark:focus,
.pi-demo__compact-btn:focus {
  outline: 2px solid #1b59f8;
  outline-offset: 2px;
}

.pi-demo__banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 14px;
  padding: 10px 14px;
  border: 1px solid #d9dde3;
  border-left: 4px solid #6b7280;
  border-radius: 4px;
  background: #f6f7f9;
}

.pi-demo__banner-text {
  flex: 1 1 320px;
  margin: 0;
  font-size: 13px;
  color: #1f2933;
}

.pi-demo__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-demo__note {
  flex: 1 1 100%;
  margin: 0;
  font-size: 12px;
  color: #3a424e;
}

.pi-demo__state {
  margin: 0 0 6px;
  font-size: 12px;
  color: #1f2933;
}

.pi-demo__error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #a3172b;
}

.pi-demo__retry {
  margin-left: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: #1b59f8;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.pi-demo__pending,
.pi-demo__notice {
  margin: 6px 0 0;
  font-size: 12px;
  color: #0f6b2b;
}

.pi-demo__pending,
.pi-demo__notice.is-pending {
  color: #7a4f00;
}

.pi-demo__overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(17, 24, 39, 0.45);
}

.pi-demo__dialog {
  width: 100%;
  max-width: 520px;
  max-height: 100%;
  overflow-y: auto;
  padding: 18px 20px;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  text-align: left;
}

.pi-demo__title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: #1f2933;
}

.pi-demo__company {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #3a424e;
}

.pi-demo__shard {
  font-weight: 400;
  color: #6b7280;
}

.pi-demo__effect {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.5;
  color: #1f2933;
}

.pi-demo__reasons {
  margin: 0 0 12px;
  padding: 0;
  border: 0;
}

.pi-demo__legend {
  margin: 0 0 6px;
  border: 0;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-demo__option {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 400;
  color: #1f2933;
}

.pi-demo__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.pi-demo__label {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #3a424e;
}

.pi-demo__label label {
  margin: 0;
  font-weight: 700;
}

.pi-demo__textarea {
  width: 100%;
  resize: vertical;
}

.pi-demo__count {
  font-size: 11px;
  color: #6b7280;
}

.pi-demo__field-error {
  display: block;
  font-size: 12px;
  color: #a3172b;
}

.pi-demo__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/components/pi/piCompanyDemo.vue